/**
 * ==========================================================================
 *  THE PORUKKA IN THE MIDDLE OF THE HALL, AND THE PRICE OF GETTING PAST THEM
 * ==========================================================================
 *  The first thing in the juhlasali is the baari and the second is everybody
 *  who has already been to it - a couple of ranks of them standing on the
 *  open floor a stride past the glasses, across the whole width of the runway,
 *  which is the only floor between the doorway and Kimmo's own chair. They are
 *  not going anywhere, and they say why on the way in:
 *
 *      "Et kai sä 'Kimmo' selvinpäin tullut pelaamaan? Ota vaikka yks sima
 *       eka."
 *
 *  Which is the toll. Two presses of F, in this order and no other:
 *
 *      tuoppi off the stack   ->  F  ->  the glass is in Kimmo's hand
 *      oikeanpuoleinen simahana -> F -> the handle goes down
 *      ...and two seconds of standing there  ->  it is full, the room opens
 *
 *  Only the right-hand PANIMOSIMA tap fills the glass.
 *
 *  ------------------------------------------------------------------------
 *   THE POUR IS THE TOLL, AND THAT IS WHY IT IS A CLOCK
 *  ------------------------------------------------------------------------
 *  The glass and the hanat are a stride apart on the same bar and the people
 *  are a stride the other way, which is what makes the errand findable (see
 *  `TUOPPIPOYTA` in src/config/level.ts) and which is also why the second
 *  press cannot be the end of it. A toll paid by pressing a key at the thing
 *  that is in the way is not a toll - it is a door. So the press only opens
 *  the handle, and what costs is the wait:
 *  `SIMA.pourDuration` of glass under the nozzle, counted one fixed step at a
 *  time, and counted only while Kimmo is still inside `SIMA.reach` of the
 *  nozzle he opened.
 *
 *  Which makes the leash the whole mechanic. Walk away and the handle springs
 *  back with whatever went in still in the glass; come back, press again, and
 *  it carries on from there. Nobody can start a pour at the bar and walk the
 *  four metres to the crowd with a glass that fills itself on the way, and
 *  nobody who stumbles - this is a room a player arrives in drunk - loses more
 *  than the second it took to stagger back.
 *
 *  ------------------------------------------------------------------------
 *   NO THREE.JS
 *  ------------------------------------------------------------------------
 *  Same split as `Door`, `Kamiina` and `Aamupala`: this owns everything the
 *  simulation can observe - what is in Kimmo's hand, how full it is, and the
 *  box the crowd is - and the people, the glasses on the table and the one in
 *  the corner of the screen are meshes that read it back out. It is stepped
 *  inside the fixed timestep and it has no clock of its own, so a run
 *  re-simulates to the same millisecond in a Lambda with no scene graph
 *  anywhere near it.
 * ==========================================================================
 */

import { SIMA } from '../config/sima';
import type { Vec3 } from '../utils/math';
import type { Collider } from './CollisionWorld';
import type { SimaSpec } from './Level';
import { isLookingAt, type InteractContext, type Interactable } from './Interactable';

/** What one step of the errand did, for the mixer and the viewmodel. */
export type TuoppiEvent = 'taken' | 'pouring' | 'poured';

export class Sima {
  /** The empty glass on top of the stack. Reach it empty-handed and F takes it. */
  readonly glass: Interactable;
  /** The two nozzles on the baari. Only the sima nozzle can be opened. */
  readonly taps: readonly Interactable[];

  private carrying = false;
  /** How much of a pint is in it, 0 .. 1. */
  private poured = 0;
  /** The nozzle whose handle is currently down, or null when none is. */
  private open: Vec3 | null = null;
  /** When the glass filled, which is what moves the crowd. Null until it does. */
  private pouredAtTime: number | null = null;
  private readonly gates: readonly Collider[];

  constructor(
    spec: SimaSpec,
    /** The box that is the porukka while they are filling the runway. */
    gate: Collider,
    additionalGates: readonly Collider[] = [],
  ) {
    this.gates = [gate, ...additionalGates];
    this.glass = {
      id: 'tyhja-tuoppi',
      prompt: 'OTA TUOPPI',
      focusPoint: () => spec.glass,
      canInteract: (context) => !this.carrying && this.inReach(context, spec.glass),
      interact: () => {
        this.carrying = true;
      },
      reset: () => undefined,
    };

    // Keep nozzle ids stable, but only the right-hand sima tap accepts F.
    this.taps = spec.taps.map((tap, index) => ({
      id: `hana-${index + 1}`,
      prompt: 'TÄYTÄ TUOPPI SIMALLA',
      focusPoint: () => tap,
      // Nothing to press while one is already running, and nothing to press
      // once the glass is full: a runner stands inside `SIMA.reach` of a
      // nozzle for a couple of hundred steps at a pour, and without both of
      // these every one of them would be another press - which is a cue firing
      // two hundred times and a key that stops being the one thing that moved
      // the crowd.
      canInteract: (context) =>
        index === SIMA.tapIndex && this.carrying && this.open === null && this.poured < 1 && this.inReach(context, tap),
      interact: () => {
        if (index === SIMA.tapIndex) this.open = tap;
      },
      reset: () => undefined,
    }));
  }

  /** True while Kimmo is running with a glass in his off hand. */
  get holdingTuoppi(): boolean {
    return this.carrying;
  }

  /** True once there is a full one in it, which is the only thing the crowd asks. */
  get isPoured(): boolean {
    return this.pouredAtTime !== null;
  }

  /** How full it is, 0 .. 1. The viewmodel draws the beer off this. */
  get fill(): number {
    return this.poured;
  }

  /** True while a handle is down and the glass is filling. */
  get isPouring(): boolean {
    return this.open !== null;
  }

  /** Simulation time the tuoppi filled, or null. Nothing but the mixer reads it. */
  get pouredAt(): number | null {
    return this.pouredAtTime;
  }

  reset(): void {
    this.carrying = false;
    this.poured = 0;
    this.open = null;
    this.pouredAtTime = null;
    for (const gate of this.gates) gate.enabled = true;
  }

  /**
   * One fixed step of the pour, and the collider the crowd is.
   *
   * `dt` rather than a clock difference, because the pour can be interrupted
   * and resumed: what is being measured is time spent *at the nozzle*, which
   * is a sum and not an interval. Accumulating the fixed step is exact enough
   * for that and, more to the point, is the same arithmetic in the same order
   * on every machine - which is what the verifier needs.
   *
   * The room opens on the step the glass fills rather than on the one after
   * it. Nobody is leaning on the box when this fires - it is four metres east
   * of the nozzle and the pour is made facing a wall - but the promise
   * `StepEvents.tuoppi` makes is exact, and a promise that is true except for
   * 8 ms is the kind that gets relied on and then quietly broken.
   *
   * No swing and no animation either, unlike the veräjä. They used to be a
   * whole hall away when this fired, which made the cut free; they are four
   * metres away now, at the back of a runner who is turned to the counter he
   * is pouring at, so what it costs is a player who deliberately turns round
   * to watch. That is worth a cut and not worth an animation: the veräjä is
   * animated because you are standing in front of it waiting for it, and this
   * is a thing that happens behind you while you hold a glass under a tap.
   */
  simulate(context: InteractContext, dt: number): TuoppiEvent | null {
    let event: TuoppiEvent | null = null;

    if (this.open) {
      if (this.withinReach(context, this.open)) {
        this.poured = Math.min(1, this.poured + dt / SIMA.pourDuration);
        if (this.poured >= 1) {
          this.pouredAtTime = context.simulationTime;
          this.open = null;
          event = 'poured';
        }
      } else {
        // Out of range, so the handle springs back. What is in the glass stays
        // in it - see `SIMA.pourDuration`.
        this.open = null;
      }
    }

    for (const gate of this.gates) gate.enabled = this.pouredAtTime === null;
    return event;
  }

  /**
   * Close enough, and roughly facing it.
   *
   * `SIMA`'s own cone rather than `INTERACT`'s, and the reasoning is in
   * `src/config/sima.ts`: these are two things you put a hand out for while
   * still moving, and the turn towards them is what the press is worth.
   */
  private inReach(context: InteractContext, point: Vec3): boolean {
    return isLookingAt(context, point, SIMA.reach, SIMA.minLookDot);
  }

  /**
   * And close enough on its own, with no cone at all.
   *
   * This is the leash the pour runs on. Holding a crosshair on a nozzle for
   * two seconds while the camera sways would be a test of the drunk meter
   * rather than of anything this errand is about, so once the handle is down
   * Kimmo may look wherever he likes; it is walking off that shuts the tap.
   */
  private withinReach(context: InteractContext, point: Vec3): boolean {
    return (
      Math.hypot(
        point.x - context.eye.x,
        point.y - context.eye.y,
        point.z - context.eye.z,
      ) <= SIMA.reach
    );
  }
}
